import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink, splitLink, httpSubscriptionLink } from "@trpc/client";
import { QueryClient } from "@tanstack/react-query";
import type { AppRouter } from "@iamu/api/src/trpc/router.js";
import { getAuthHeaders } from "./auth";
import { toast } from "sonner";

export const trpc = createTRPCReact<AppRouter>();

import {
  handleLimitExceededError,
  handleAuthError,
  isAuthError,
  toastTrpcError,
  formatTrpcErrorMessage,
} from "./trpc-error.js";

export {
  handleLimitExceededError,
  handleAuthError,
  isAuthError,
  toastTrpcError,
  formatTrpcErrorMessage,
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry(failureCount, error) {
        // Don't retry auth errors — redirect to login
        if (isAuthError(error)) return false;
        const code =
          error && typeof error === "object" && "data" in error
            ? (error as { data?: { code?: string } }).data?.code
            : undefined;
        if (code === "UNAUTHORIZED" || code === "FORBIDDEN") return false;
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        handleLimitExceededError(error);
        handleAuthError(error);
      },
    },
  },
});

// Global query error handler — catches auth & quota failures across all queries
queryClient.getQueryCache().config.onError = (error) => {
  handleLimitExceededError(error);
  handleAuthError(error);
};

// Global mutation error fallback — ensures unhandled mutation failures show safe toasts
queryClient.getMutationCache().config.onError = (
  error,
  _variables,
  _context,
  mutation,
) => {
  if (!mutation?.options?.onError) {
    toastTrpcError(error);
  } else {
    handleLimitExceededError(error);
    handleAuthError(error);
  }
};

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition: (op) => op.type === "subscription",
      true: httpSubscriptionLink({
        url: "/trpc",
        // Native EventSource sends cookies automatically — no polyfill needed
      }),
      false: httpBatchLink({
        url: "/trpc",
        headers: () => getAuthHeaders(),
        fetch: (url, options) =>
          fetch(url, { ...options, credentials: "include" }),
      }),
    }),
  ],
});
